import { createIntl, EditableProTable, ProProvider } from '@ant-design/pro-components'
import { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { message } from 'antd'

/**
 * Zwei Betriebsarten:
 * - request-Modus (Default): die Tabelle laedt ihre Daten selbst ueber `props.request`
 *   und laedt nach jedem Speichern/Loeschen erneut.
 * - kontrollierter Modus (HOR-93): der Aufrufer uebergibt `dataSource` (memoisiert!) und
 *   `onReload`; die Tabelle laedt dann nie selbst, sondern ruft nach Speichern/Loeschen
 *   `onReload` auf, damit Tabelle und abgeleitete Anzeigen (z.B. Summen) aus EINER Quelle
 *   kommen. `loading` kann der Aufrufer zusaetzlich setzen.
 */
export default props => {
  useTranslation()
  const controlled = Array.isArray(props.dataSource)
  const [loading, setLoading] = useState(!controlled)
  const [editableKeys, setEditableRowKeys] = useState([])
  const [dataSource, setDataSource] = useState(controlled ? props.dataSource : [])
  const [randomKey, setRandomKey] = useState(null)

  // HOR-93: die refresh-Funktion wird einmalig beim Mount nach aussen gegeben, muss aber
  // immer die aktuellen Props sehen (onReload des Aufrufers kann sich pro Render aendern)
  const propsRef = useRef(props)
  propsRef.current = props

  useEffect(() => {
    if (props.refresh)
      props.refresh(() => () => {
        if (Array.isArray(propsRef.current.dataSource)) {
          propsRef.current.onReload?.()
          return
        }

        setRandomKey(Math.random())
      })
  }, [])

  // HOR-93: kontrollierter Modus – Aenderungen des Aufrufers in den lokalen Zustand
  // uebernehmen. Der lokale Zustand bleibt noetig, weil EditableProTable neue (noch nicht
  // gespeicherte) Zeilen ueber onChange in die value-Liste schreibt.
  useEffect(() => {
    if (controlled) setDataSource(props.dataSource)
  }, [controlled, props.dataSource])

  // Nachladen nach Speichern/Loeschen: im kontrollierten Modus laedt der Aufrufer, sonst
  // die Tabelle selbst. Rueckgabe: neue Zeilen (request-Modus) oder null (kontrolliert).
  const reload = async () => {
    if (controlled) {
      await props.onReload?.()

      return null
    }

    const response = await props.request()

    return response?.data
  }

  /**
   * Frisch geladene Zeilen nach dem Speichern VERZOEGERT uebernehmen: EditableProTable ersetzt
   * nach dem Resolve von onSave die editierte Zeile in seiner ALTEN Liste und ruft damit
   * onChange (pro-utils useEditableArray actionSaveRef) – ein sofortiges Setzen wuerde also
   * ueberschrieben, die 'null_'-Zeile bliebe stehen. HOR-93: im kontrollierten Modus kommen die
   * Zeilen aus propsRef (der Aufrufer hat sie ueber onReload bereits neu gesetzt).
   */
  const applyReloadedRows = rows => {
    setTimeout(() => {
      const next = Array.isArray(rows) ? rows : controlled ? propsRef.current.dataSource : null

      if (Array.isArray(next)) setDataSource(next)
    }, 100)
  }

  const enUSIntl = createIntl('en_US', {
    tableForm: {
      inputPlaceholder: i18n('common.enterValue'),
      selectPlaceholder: i18n('common.selectValue'),
    },
    table: {
      emptyText: i18n('common.noData'),
    },
    editableTable: {
      emptyText: i18n('common.noData'),
      action: {
        add: i18n('common.addRecord'),
        delete: i18n('common.delete'),
        save: i18n('common.save'),
        cancel: i18n('common.cancel'),
      },
    },
  })

  const values = useContext(ProProvider)

  const [messageApi, messageContextHolder] = message.useMessage()

  // HOR-57: Lange Texte (Artikelbezeichnungen, Kundenkommentare) haben die Zeilenhoehe
  // unbegrenzt wachsen lassen. Ellipsis haelt jede Zelle einzeilig und zeigt den vollen
  // Text im Tooltip. Einzelne Spalten koennen mit `ellipsis: false` ausscheren.
  //
  // HOR-88/90/91: Der Default gilt nur fuer Spalten OHNE eigenes `render`. Pro-Table
  // wickelt bei `ellipsis` den Zellwert in ein <Typography.Text ellipsis> und uebergibt
  // dieses React-Element (nicht den Rohwert) als ERSTES Argument an `render` – Spalten
  // wie `val => moment(val).fromNow()` zeigten dadurch Unsinn ("vor 4 Stunden" ueberall,
  // Status "Unbekannt", "[object Object] Leistung(en)"). Bei render-Spalten wird das
  // Element ohnehin verworfen, es blieb nur die td-CSS ohne Tooltip. Wer es braucht,
  // setzt `ellipsis: true` explizit und liest den Rohwert aus `record`.
  const columns = [
    ...props.columns.map(column => ({ ellipsis: typeof column.render !== 'function', ...column })),
    ...(props.modes.includes('edit') || props.modes.includes('delete')
      ? [
          {
            title: i18n('common.actions'),
            valueType: 'option',
            key: 'actions',
            // Aktionsspalte nie kuerzen, sonst verschwinden die Links.
            ellipsis: false,
            render: (text, record, _, action) => [
              ...(props.modes.includes('edit')
                ? [
                    <a
                      key="editable"
                      onClick={() => {
                        action?.startEditable?.(record.id)
                      }}
                    >
                      {i18n('common.edit')}
                    </a>,
                  ]
                : []),
              ...(props.modes.includes('delete')
                ? [
                    <a
                      key="delete"
                      onClick={async () => {
                        setLoading(true)

                        const res = await props.onDelete(record)

                        if (!res)
                          messageApi.open({
                            type: 'error',
                            content: i18n('common.requestFailed'),
                          })

                        const rows = await reload()

                        setLoading(false)

                        if (rows) setDataSource(rows)
                      }}
                    >
                      {i18n('common.delete')}
                    </a>,
                  ]
                : []),
            ],
          },
        ]
      : []),
  ]

  return (
    // HOR-81: Hier lag ein `maxHeight: 768, overflowY: 'auto'` um die Tabelle. Das ergab
    // einen zweiten Scroll-Container um den, den Ant Design wegen `scroll={{x: 960}}`
    // ohnehin selbst rendert. Bei hohen Zeilen blockierten sich die verschachtelten
    // Container gegenseitig, die Artikelverwaltung liess sich nicht mehr scrollen.
    // Die Seite scrollt jetzt normal, der Tabellenkopf bleibt beim Scrollen stehen.
    <div>
      <ProProvider.Provider
        value={{
          ...values,
          intl: enUSIntl,
        }}
      >
        <EditableProTable
          rowKey="id"
          headerTitle={props.title}
          locale={{ emptyText: i18n('common.noData') }}
          params={{
            randomKey,
          }}
          scroll={{
            x: 960,
          }}
          sticky
          recordCreatorProps={
            props.modes.includes('create')
              ? {
                  top: 'bottom',
                  newRecordType: 'dataSource',
                  // HOR-51: Eine neue Zeile startete komplett leer, Zahlenfelder standen
                  // damit auf 0. `newRecordDefaults` laesst den Aufrufer sinnvolle
                  // Startwerte setzen (z.B. Anzahl = 1 bei KVA-Positionen).
                  record: () => ({ id: 'null_' + (Math.random() * 1000000).toFixed(0), ...(props.newRecordDefaults || {}) }),
                }
              : false
          }
          loading={loading || !!props.loading}
          columns={columns}
          // HOR-93: im kontrollierten Modus laedt die Tabelle nie selbst
          request={
            controlled
              ? undefined
              : async () => {
                  const data = await props.request()

                  setLoading(false)

                  return data
                }
          }
          value={props.filter ? props.filter(dataSource) : dataSource}
          onChange={newData => {
            setDataSource(newData)
          }}
          editable={{
            type: 'single',
            editableKeys,
            deletePopconfirmMessage: i18n('common.deleteRecord'),
            onlyAddOneLineAlertMessage: i18n('common.onlyOneCreate'),
            onlyOneLineEditorAlertMessage: i18n('common.onlyOneEdit'),
            onSave: async (rowKey, data) => {
              setLoading(true)

              if (String(rowKey).startsWith('null_')) {
                const res = await props.onCreate(data)

                if (!res)
                  messageApi.open({
                    type: 'error',
                    content: i18n('common.requestFailed'),
                  })

                const rows = await reload()

                setLoading(false)

                applyReloadedRows(rows)
              } else {
                const res = await props.onEdit(data)

                if (!res)
                  messageApi.open({
                    type: 'error',
                    content: i18n('common.requestFailed'),
                  })

                const rows = await reload()

                setLoading(false)

                applyReloadedRows(rows)
              }
            },
            actionRender: (row, config, dom) => {
              return (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {Object.keys(dom).map(key => {
                    if (!dom[key]) return null
                    const Object = dom[key].type

                    if (key === 'cancel' && String(row.id).startsWith('null_')) return null
                    if (key === 'delete' && !String(row.id).startsWith('null_')) return null

                    if (key === 'delete' && !props.modes.includes('delete')) return null

                    return <Object key={key} {...dom[key].props} />
                  })}
                </div>
              )
            },
            onChange: setEditableRowKeys,
          }}
        />
      </ProProvider.Provider>
      {messageContextHolder}
    </div>
  )
}
